import { useEffect, useState } from "react";
import { getInventoryValuation, listInventoryItems, listLowStockAlerts, type AdminSession } from "@/lib/api";
import { useSession } from "@/auth/session";
import CategoriasSection from "./sections/CategoriasSection";
import ProductosSection from "./sections/ProductosSection";
import ProveedoresSection from "./sections/ProveedoresSection";
import ComprasSection from "./sections/ComprasSection";
import TransferenciasSection from "./sections/TransferenciasSection";
import MovimientosSection from "./sections/MovimientosSection";

type TabKey = "categorias" | "productos" | "proveedores" | "compras" | "transferencias" | "movimientos";

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "categorias", label: "Categorías" },
  { key: "productos", label: "Productos" },
  { key: "proveedores", label: "Proveedores" },
  { key: "compras", label: "Compras" },
  { key: "transferencias", label: "Transferencias" },
  { key: "movimientos", label: "Movimientos de stock" },
];

export default function BodegaPage() {
  const { session } = useSession();
  const [activeTab, setActiveTab] = useState<TabKey>("categorias");

  const [valuation, setValuation] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function loadStats(currentSession: AdminSession): Promise<void> {
      const settled = await Promise.allSettled([
        getInventoryValuation(currentSession),
        listInventoryItems(currentSession),
        listLowStockAlerts(currentSession),
      ] as const);
      if (cancelled) return;
      if (settled[0].status === "fulfilled") setValuation(settled[0].value.totalValue);
      if (settled[1].status === "fulfilled") setProductCount(settled[1].value.length);
      if (settled[2].status === "fulfilled") setLowStockCount(settled[2].value.length);
    }
    if (session) void loadStats(session);
    return () => {
      cancelled = true;
    };
  }, [session]);

  if (!session) return null;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-800">Bodega / Inventario</h1>
        <p className="mt-0.5 text-sm text-gray-500">Mantenedor de categorías, productos, proveedores, compras, transferencias y movimientos de stock</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard label="Valor total inventario" value={`$${Math.round(valuation).toLocaleString("es-CL")}`} />
        <StatCard label="Productos" value={String(productCount)} />
        <StatCard label="Alertas bajo stock" value={String(lowStockCount)} />
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-2">
        <div className="flex gap-2 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-shrink-0 whitespace-nowrap rounded-lg px-4 py-2 text-sm ${
                activeTab === tab.key ? "bg-blue-600 text-white" : "border border-gray-200 text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === "categorias" ? <CategoriasSection session={session} /> : null}
      {activeTab === "productos" ? <ProductosSection session={session} /> : null}
      {activeTab === "proveedores" ? <ProveedoresSection session={session} /> : null}
      {activeTab === "compras" ? <ComprasSection session={session} /> : null}
      {activeTab === "transferencias" ? <TransferenciasSection session={session} /> : null}
      {activeTab === "movimientos" ? <MovimientosSection session={session} /> : null}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-1 text-xl font-bold text-gray-800">{value}</div>
    </div>
  );
}
